import React, { useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ArrowRight, School, Wrench, UserCheck, CheckCircle, Phone, Mail, Award, Trophy, Medal } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PartenariatEcolePage = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', contact_name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.contact_name,
        email: `partenariat-${form.name.replace(/\s/g,'')}@ludotech.ci`,
        phone: form.phone,
        message: `[Demande partenariat école: ${form.name}] ${form.message}`,
      });
      toast.success('Demande envoyée avec succès !');
      setForm({ name: '', contact_name: '', phone: '', message: '' });
    } catch { toast.error("Erreur lors de l'envoi."); }
    finally { setLoading(false); }
  };

  const steps = [
    { num: '01', title: "Échange avec l'école", desc: "Nous définissons ensemble vos besoins, le public cible et le format idéal (périscolaire, intégré au cursus, stage)." },
    { num: '02', title: 'Mise en place', desc: "Nous planifions les interventions, préparons le matériel spécifique et affectons nos formateurs dédiés." },
    { num: '03', title: 'Intervention', desc: "Nos équipes se rendent dans votre établissement avec tout l'équipement pour animer les séances." },
    { num: '04', title: 'Suivi et bilan', desc: "Nous évaluons la progression des élèves et vous fournissons un retour régulier sur le déroulement des ateliers." },
  ];

  const interventions = [
    { title: 'Robotique primaire', desc: "Initiation douce avec des robots éducatifs simples, découverte de la logique séquentielle et repérage dans l'espace." },
    { title: 'Programmation collège', desc: "Programmation par blocs (Scratch, mBlock), création de jeux vidéo et contrôle de robots plus complexes." },
    { title: 'Projets avancés lycée', desc: "Introduction à la programmation textuelle (Python, C++), modélisation 3D et conception de systèmes automatisés." },
  ];

  return (
    <Layout>
      {/* HERO */}
      <section className="bg-[#1B6FE3] py-12 md:py-20 relative overflow-hidden" data-testid="partenariat-hero">
        <div className="absolute top-10 right-10 w-64 h-64 bg-white/5 rounded-full"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block bg-white/15 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-bold text-white/90 mb-5">
                POUR LES ÉTABLISSEMENTS SCOLAIRES
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-black text-white mb-5 leading-tight">
                Intégrez la robotique dans votre école
              </h1>
              <p className="text-base text-white/80 mb-8 leading-relaxed">
                Nous proposons des cours de robotique et programmation directement dans votre établissement. Préparez vos élèves aux défis technologiques de demain.
              </p>
              <a href="#contact-form" className="btn-primary text-base px-8 py-4 inline-flex items-center gap-2">
                Demander un partenariat <ArrowRight size={18} />
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex justify-center">
              <div className="relative">
                <div className="w-80 h-80 rounded-full bg-sky-300/20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
                <img src="https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/571217501_122263978868204093_7414678444280471226_n-2-4Wll7.jpg"
                  alt="Enfants apprenant la robotique en classe" className="relative z-10 rounded-2xl shadow-2xl w-full max-w-md" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="py-16 md:py-20 bg-white" data-testid="vision-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm font-bold text-[#1B6FE3] uppercase tracking-wider mb-2">VISION ÉDUCATIVE</p>
              <h2 className="font-heading text-3xl font-extrabold text-slate-900 mb-4">Un enjeu majeur pour les écoles</h2>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Le numérique transforme notre société à une vitesse fulgurante. Il est devenu indispensable de doter les élèves des compétences nécessaires pour comprendre et maîtriser ces technologies, plutôt que de les subir.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed">
                En intégrant la robotique et la programmation dans votre cursus, vous offrez à vos élèves une opportunité unique de développer leur pensée logique, leur créativité et leur capacité à résoudre des problèmes complexes dans un cadre collaboratif.
              </p>
            </div>
            <img src="https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/peg-code-partenariat-cPXLx.png"
              alt="Élèves concentrés" className="rounded-2xl shadow-lg w-full" />
          </div>
        </div>
      </section>

      {/* SOLUTION CLÉ EN MAIN */}
      <section className="py-16 md:py-20 section-light-blue" data-testid="solution-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-3">Notre solution clé en main</h2>
          <p className="text-slate-600 text-center mb-10 max-w-xl mx-auto">Une approche simplifiée pour intégrer l'innovation sans contrainte logistique.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { icon: <School className="w-7 h-7 text-[#1B6FE3]" />, title: 'Nous intervenons dans votre école', desc: "Pas besoin de déplacer vos élèves. Nos équipes se rendent directement dans votre établissement." },
              { icon: <Wrench className="w-7 h-7 text-[#F97316]" />, title: 'Nous fournissons le matériel', desc: "Robots, tablettes, ordinateurs... Nous apportons tout l'équipement nécessaire. Aucun investissement matériel requis." },
              { icon: <UserCheck className="w-7 h-7 text-emerald-500" />, title: 'Nos formateurs encadrent', desc: "Nos animateurs spécialisés prennent en charge l'animation complète des ateliers." },
            ].map((s, i) => (
              <div key={i} className="tk-card text-center">
                <div className="w-14 h-14 bg-slate-50 rounded-xl flex items-center justify-center mx-auto mb-3">{s.icon}</div>
                <h3 className="font-heading text-base font-extrabold text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-slate-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERVENTIONS ADAPTÉES */}
      <section className="py-16 md:py-20 bg-white" data-testid="interventions-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-10">Nos interventions adaptées</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {interventions.map((item, i) => (
              <div key={i} className="tk-card border-t-4 border-t-[#1B6FE3]">
                <h3 className="font-heading text-lg font-extrabold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BÉNÉFICES */}
      <section className="py-14 section-cream" data-testid="benefits-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <h2 className="font-heading text-2xl font-extrabold text-slate-900 text-center mb-6">Bénéfices pour votre établissement</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Valorise l'image innovante de votre école",
              "Développe les compétences transversales des élèves",
              "Attire et fidélise les parents d'élèves",
              "Différencie votre établissement sur le plan pédagogique",
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-20 bg-white" data-testid="process-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-3">Comment ça marche ?</h2>
          <p className="text-slate-600 text-center mb-10">Un processus simple et transparent pour mettre en place les ateliers.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="tk-card text-center">
                <div className="text-3xl font-black text-[#1B6FE3]/20 mb-2">{s.num}</div>
                <h3 className="font-heading text-base font-extrabold text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-slate-600 text-sm">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPÉTITION */}
      <section className="py-16 md:py-20 bg-[#1B6FE3] text-white" data-testid="competition-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/15 px-4 py-1.5 rounded-full text-sm font-bold mb-4">ÉVÉNEMENT EXCLUSIF</div>
          <h2 className="font-heading text-3xl md:text-4xl font-black mb-4">Compétition Inter-École Annuelle</h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-10 text-sm">
            Rejoignez notre grande compétition de fin d'année ! Un événement prestigieux où les élèves des différentes écoles partenaires s'affrontent sur des défis passionnants de robotique et de programmation.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {[
              { icon: <Medal className="w-8 h-8" />, place: '1ère Place', title: "Médaille d'Or", desc: "Attribuée à l'équipe championne pour sa créativité et son excellence technique." },
              { icon: <Medal className="w-8 h-8" />, place: '2ème Place', title: "Médaille d'Argent", desc: "Récompense la deuxième meilleure équipe pour sa rigueur et ses solutions innovantes." },
              { icon: <Medal className="w-8 h-8" />, place: '3ème Place', title: 'Médaille de Bronze', desc: "Célèbre l'effort, le travail d'équipe et l'ingéniosité de la troisième équipe finaliste." },
              { icon: <Trophy className="w-8 h-8" />, place: 'Prix Spécial', title: 'Trophée Itinérant', desc: "La coupe ultime ! L'école gagnante conserve fièrement ce trophée prestigieux pendant un an." },
            ].map((p, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center">
                <div className="text-[#F97316] mb-2 flex justify-center">{p.icon}</div>
                <p className="text-xs font-bold text-white/60 uppercase">{p.place}</p>
                <h3 className="font-heading text-lg font-extrabold mb-1">{p.title}</h3>
                <p className="text-white/70 text-xs">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact-form" className="py-16 md:py-20 bg-white" data-testid="partenariat-contact">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-4">Parlons de votre projet éducatif</h2>
              <p className="text-slate-600 text-sm mb-6">
                Prêt à faire entrer votre établissement dans l'ère de la robotique ? Remplissez ce formulaire et notre équipe vous contactera.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3"><Phone size={16} className="text-[#1B6FE3]" /><span className="text-slate-700 text-sm font-semibold">+225 0718463949</span></div>
                <div className="flex items-center gap-3"><Mail size={16} className="text-[#F97316]" /><span className="text-slate-700 text-sm font-semibold">contact@ludotech-robotics.com</span></div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="tk-card space-y-4" data-testid="partenariat-form">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Nom de l'établissement *</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Nom du contact (Directeur/Responsable) *</label>
                <input required value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Téléphone</label>
                <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Votre projet / Message *</label>
                <textarea required rows={4} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3" data-testid="submit-partenariat">
                {loading ? 'Envoi...' : 'Envoyer la demande'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PartenariatEcolePage;
